// src/pages/finance-portal/payroll-settings/PayrollSettings.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, MenuItem, Select,
    FormControl, InputLabel, InputAdornment, CircularProgress, Alert,
    Autocomplete,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const cardSx = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    mb: 3,
};

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

const sectionTitle = {
    fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b',
};

const GL_MAPPING_FIELDS = [
    { key: 'gl_salaries_expense', label: 'Salaries & Wages Expense' },
    { key: 'gl_payroll_tax_expense', label: 'Payroll Tax Expense' },
    { key: 'gl_payroll_liability', label: 'Payroll Liabilities' },
    { key: 'gl_cash_account', label: 'Cash / Bank Account' },
    { key: 'gl_benefits_expense', label: 'Benefits Expense' },
];

export default function PayrollSettings() {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [glAccounts, setGlAccounts] = useState([]);
    const [glLoading, setGlLoading] = useState(false);

    const [form, setForm] = useState({
        company_legal_name: '',
        ein: '',
        company_address_street: '',
        company_address_city: '',
        company_address_state: '',
        company_address_zip: '',
        ss_rate: '6.2',
        medicare_rate: '1.45',
        futa_rate: '0.6',
        suta_rate: '2.7',
        overtime_multiplier: '1.5',
        overtime_threshold_hours: '40',
        default_state: '',
        default_state_tax_rate: '',
        gl_salaries_expense: null,
        gl_payroll_tax_expense: null,
        gl_payroll_liability: null,
        gl_cash_account: null,
        gl_benefits_expense: null,
    });

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/payroll/settings');
            const cfg = res.data?.data || res.data || {};
            setForm(prev => ({
                ...prev,
                company_legal_name: cfg.company_legal_name || '',
                ein: cfg.ein || '',
                company_address_street: cfg.company_address_street || '',
                company_address_city: cfg.company_address_city || '',
                company_address_state: cfg.company_address_state || '',
                company_address_zip: cfg.company_address_zip || '',
                ss_rate: cfg.ss_rate ?? '6.2',
                medicare_rate: cfg.medicare_rate ?? '1.45',
                futa_rate: cfg.futa_rate ?? '0.6',
                suta_rate: cfg.suta_rate ?? '2.7',
                overtime_multiplier: cfg.overtime_multiplier ?? '1.5',
                overtime_threshold_hours: cfg.overtime_threshold_hours ?? '40',
                default_state: cfg.default_state || '',
                default_state_tax_rate: cfg.default_state_tax_rate ?? '',
                gl_salaries_expense: cfg.gl_salaries_expense || null,
                gl_payroll_tax_expense: cfg.gl_payroll_tax_expense || null,
                gl_payroll_liability: cfg.gl_payroll_liability || null,
                gl_cash_account: cfg.gl_cash_account || null,
                gl_benefits_expense: cfg.gl_benefits_expense || null,
            }));
        } catch {
            enqueueSnackbar('Failed to load payroll settings.', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    const fetchGlAccounts = useCallback(async () => {
        setGlLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) {
                setGlAccounts(res.data.data || []);
            }
        } catch {
            // silent
        } finally {
            setGlLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
        fetchGlAccounts();
    }, [fetchSettings, fetchGlAccounts]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axiosInstance.put('/api/finance/payroll/settings', form);
            enqueueSnackbar('Payroll settings saved.', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save payroll settings.', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const findGlAccount = (id) => {
        if (!id) return null;
        return glAccounts.find(a => a.id === id) || null;
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: '16px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '12px',
                        bgcolor: 'rgba(255,255,255,0.2)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <SettingsIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700 }}>
                            Payroll Settings
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.9rem', opacity: 0.9 }}>
                            Configure company info, tax rates, overtime rules, and GL mappings
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: ACCENT }} />
                </Box>
            ) : (
                <>
                    {/* Company Info */}
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={sectionTitle}>Company Information</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                            <TextField
                                label="Legal Company Name"
                                size="small"
                                value={form.company_legal_name}
                                onChange={e => handleChange('company_legal_name', e.target.value)}
                                sx={inputSx}
                            />
                            <TextField
                                label="EIN (Employer Identification Number)"
                                size="small"
                                value={form.ein}
                                onChange={e => handleChange('ein', e.target.value)}
                                placeholder="XX-XXXXXXX"
                                sx={inputSx}
                            />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                            <TextField
                                label="Street Address"
                                size="small"
                                value={form.company_address_street}
                                onChange={e => handleChange('company_address_street', e.target.value)}
                                sx={inputSx}
                            />
                            <TextField
                                label="City"
                                size="small"
                                value={form.company_address_city}
                                onChange={e => handleChange('company_address_city', e.target.value)}
                                sx={inputSx}
                            />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <TextField
                                label="State"
                                size="small"
                                value={form.company_address_state}
                                onChange={e => handleChange('company_address_state', e.target.value)}
                                placeholder="e.g. CA"
                                sx={inputSx}
                            />
                            <TextField
                                label="ZIP Code"
                                size="small"
                                value={form.company_address_zip}
                                onChange={e => handleChange('company_address_zip', e.target.value)}
                                sx={inputSx}
                            />
                        </Box>
                    </Paper>

                    {/* Tax Rates */}
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={sectionTitle}>Tax Rates</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 2.5 }}>
                            Default federal and state payroll tax rates. These are used for payroll calculations.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2.5 }}>
                            <TextField
                                label="Social Security Rate"
                                size="small"
                                type="number"
                                value={form.ss_rate}
                                onChange={e => handleChange('ss_rate', e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                helperText="Default: 6.2%"
                                sx={inputSx}
                            />
                            <TextField
                                label="Medicare Rate"
                                size="small"
                                type="number"
                                value={form.medicare_rate}
                                onChange={e => handleChange('medicare_rate', e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                helperText="Default: 1.45%"
                                sx={inputSx}
                            />
                            <TextField
                                label="FUTA Rate"
                                size="small"
                                type="number"
                                value={form.futa_rate}
                                onChange={e => handleChange('futa_rate', e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                helperText="Default: 0.6%"
                                sx={inputSx}
                            />
                            <TextField
                                label="SUTA Rate"
                                size="small"
                                type="number"
                                value={form.suta_rate}
                                onChange={e => handleChange('suta_rate', e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                helperText="Default: 2.7%"
                                sx={inputSx}
                            />
                        </Box>
                    </Paper>

                    {/* Overtime */}
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={sectionTitle}>Overtime Configuration</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <TextField
                                label="Overtime Multiplier"
                                size="small"
                                type="number"
                                value={form.overtime_multiplier}
                                onChange={e => handleChange('overtime_multiplier', e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">x</InputAdornment>,
                                }}
                                helperText="Default: 1.5x"
                                sx={inputSx}
                            />
                            <TextField
                                label="Overtime Threshold (Weekly Hours)"
                                size="small"
                                type="number"
                                value={form.overtime_threshold_hours}
                                onChange={e => handleChange('overtime_threshold_hours', e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">hrs</InputAdornment>,
                                }}
                                helperText="Default: 40 hours"
                                sx={inputSx}
                            />
                        </Box>
                    </Paper>

                    {/* State Config */}
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={sectionTitle}>State Configuration</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <TextField
                                label="Default State"
                                size="small"
                                value={form.default_state}
                                onChange={e => handleChange('default_state', e.target.value)}
                                placeholder="e.g. CA"
                                sx={inputSx}
                            />
                            <TextField
                                label="Default State Tax Rate"
                                size="small"
                                type="number"
                                value={form.default_state_tax_rate}
                                onChange={e => handleChange('default_state_tax_rate', e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                sx={inputSx}
                            />
                        </Box>
                    </Paper>

                    {/* GL Account Mappings */}
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={sectionTitle}>GL Account Mappings</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 2.5 }}>
                            Map payroll transactions to your chart of accounts for journal entry generation.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            {GL_MAPPING_FIELDS.map(({ key, label }) => (
                                <Autocomplete
                                    key={key}
                                    size="small"
                                    options={glAccounts}
                                    getOptionLabel={opt => opt ? `${opt.account_number || ''} - ${opt.name || opt.account_name || ''}` : ''}
                                    value={findGlAccount(form[key])}
                                    onChange={(_, val) => handleChange(key, val ? val.id : null)}
                                    loading={glLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={label}
                                            InputProps={{
                                                ...params.InputProps,
                                                sx: { fontFamily, borderRadius: '10px', fontSize: '0.85rem' },
                                                endAdornment: (
                                                    <>
                                                        {glLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            InputLabelProps={{ sx: { fontFamily } }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    sx={{ minWidth: 200 }}
                                    noOptionsText="No accounts found"
                                />
                            ))}
                        </Box>
                    </Paper>

                    {/* Save Button */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none',
                                borderRadius: '10px', bgcolor: ACCENT,
                                '&:hover': { bgcolor: '#16a34a' }, px: 4,
                            }}
                        >
                            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Settings'}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}
